import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { showError, showInfo, showSuccess } from '../../../shared/ui/appToast';
import { presentUnexpectedFailure } from '../../../shared/sentry/presentUnexpectedFailure';
import { formatEditableMoneyAmount, parseDecimalInput, validatePositivePriceField } from '../../../shared/numeric/localeNumeric';
import type { CatalogProductRow } from '../../domain/catalogTypes';
import { CatalogDomainError } from '../../domain/catalogErrors';
import {
  CatalogPlanLimitExceededError,
  assertWithinProductPlanLimit,
  getCatalogPlanLimitExceededMessage,
} from '../../application/catalogPlanLimits';
import { createProduct, getProductById, updateProduct, validateSku, validateBarcode } from '../../infra/productsRepo';
import { patchLocalProductSkuFromServer } from '../../infra/productSkuServerPatch';
import { trimProductSku } from '../../infra/productSkuServerPatchPolicy';
import { listServiceProductsForPicker, type ServiceProductPickerOption } from '../../infra/catalogReadRepo';
import { createVariant, deactivateVariant, listVariantsForProduct, listInactiveVariantsForProduct, reactivateVariant, updateVariant } from '../../infra/productVariantsRepo';
import { adjustStock, receiveStock } from '../../../inventory/public-api'
import { ensureVariantStockRow, findStockRow, findStockRowsForVariant } from '../../../inventory/public-api';
import { getCatalogRamSnapshotCache } from '../../../catalog/infra/catalogRamSnapshotCache';
import { buildProductDraftFromSuggestion } from '../../infra/productCatalogSearch';
import { ivaRateToTaxClass, taxClassToIvaRate } from '../../domain/catalogProductDefaults';
import { isLegacyProductCategoryHint } from '../../domain/legacyCategoryId';
import { duplicateProductDraftName, emptyDraft, emptyVariantDraft, type ProductDraft, type ProductVariantDraft } from '../../domain/productDraft';
import type { CatalogStockCheckResult } from '../../domain/catalogProductConfirm';
import { buildVariantMatrixDrafts, parseAxisValues, suggestInternalVariantBarcode, type AxisInput } from '../../domain/variantMatrix';
import { calculateMarginPercent, calculateMarginValue, calculateSuggestedPrice } from '../catalogScreen.utils';
import type { CatalogProductBranchStockView } from '../../domain/catalogTypes';
import type { CategoryOption } from '../../domain/categoryOptions';
import type { PlanEntitlementsRow } from '../../../shell/feature-flags/domain/planEntitlements';
import type { ProductLookupResult } from '../../domain/barcodeLookup';
import type {
  CatalogProductDetailTab,
  CatalogProductFormMode,
} from '../../store/useCatalogOverlayStore';
import { useCatalogOverlayStore } from '../../store/useCatalogOverlayStore';
import { pickProductImage, isProductImageValidationError } from '../../infra/pickProductImage';
import { pickProductVideo, isProductVideoValidationError } from '../../infra/pickProductVideo';
import { uploadProductImage, uploadProductVariantImage, uploadProductAxisImage, removeProductImage, removeProductVariantImage, removeProductAxisImage, uploadProductVideo, removeProductVideo } from '../../infra/productImageRepo';
import { optimizeProductImage } from '../../infra/optimizeProductImage';
import { requireSessionOrganizationId } from '../../../shell/session/sessionOrganization';
import { subscribeCatalogSynced } from '../../../sync/core/catalogSyncListeners';
import { catalogItemNoun } from '../../domain/catalogTerminology';
import { trackOnboardingFunnel } from '../../../onboarding/application/onboardingFunnel';
import { hapticSuccess } from '../../../shared/ui/haptics';
import { validateComboDraft, type ComboDraftComponent } from '../../domain/productCombos';
import { upsertProductCombo } from '../../infra/productCombosRepo';
import { invalidateComboParentProductIdsCache } from '../../infra/comboParentProductIds';
import { fetchCatalogComboMetrics } from '../../infra/comboMetricsRepo';
import { requestCatalogProductDetail } from '../../navigation/requestCatalogProductDetail';
import {
  readCatalogCreateFormLayout,
  writeCatalogCreateFormLayout,
} from '../../application/catalogCreateFormLayoutPreference';
type StockRow = { quantity: number; avgCost: number } | null;

function toCatalogProductBranchStockView(row: StockRow): CatalogProductBranchStockView | null {
  if (!row) return null;
  return { quantity: row.quantity, avgCost: row.avgCost };
}

type RemoteHit = Extract<ProductLookupResult, { kind: 'remote_hit' }>;

// ─── Product branch stock ───────────────────────────────────────────────────

function useProductBranchStock({ branchId, productId, warnLabel, getStockFn }: {
  branchId: string | null;
  productId: string | null;
  warnLabel: string;
  getStockFn: (branchId: string, productId: string) => Promise<StockRow>;
}) {
  const [stock, setStock] = useState<CatalogProductBranchStockView | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [stockEpoch, setStockEpoch] = useState(0);
  const getStockFnRef = useRef(getStockFn);
  getStockFnRef.current = getStockFn;
  const requestKey = `${branchId ?? ''}:${productId ?? ''}`;
  const lastLoadedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    return subscribeCatalogSynced(() => {
      setStockEpoch((n) => n + 1);
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadStock = async () => {
      if (!branchId || !productId) {
        setStock(null);
        setLoading(false);
        setFailed(false);
        lastLoadedKeyRef.current = null;
        return;
      }
      const showLoading = lastLoadedKeyRef.current !== requestKey;
      if (showLoading) setLoading(true);
      try {
        const row = await getStockFnRef.current(branchId, productId);
        if (!cancelled) {
          setStock(toCatalogProductBranchStockView(row));
          setFailed(false);
          lastLoadedKeyRef.current = requestKey;
        }
      } catch (error) {
        console.warn(`[Catalog] No se pudo cargar el costo ${warnLabel}`, error);
        if (!cancelled) {
          // Fallo de lectura: el stock queda "sin verificar" — nunca como cero.
          setStock(null);
          setFailed(true);
          lastLoadedKeyRef.current = requestKey;
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void loadStock();
    return () => { cancelled = true; };
  }, [branchId, productId, requestKey, warnLabel, stockEpoch]);

  const retry = useCallback(() => {
    lastLoadedKeyRef.current = null;
    setStockEpoch((n) => n + 1);
  }, []);

  return { stock, loading, failed, retry };
}

// ─── Validation ─────────────────────────────────────────────────────────────

function buildProductDraftValidationMessage(
  draft: {
    name: string;
    sku: string;
    barcode: string;
    price: string;
    categoryId: string | null;
    itemType: 'physical' | 'service';
    durationMinutes: string;
    isPublic: boolean;
    bookingMode?: 'direct' | 'quote_required';
  },
  opts: { appointmentsEnabled: boolean; requireCategory: boolean },
): string | null | undefined {
  const skuValidationMessage = validateSku(draft.sku);
  const barcodeValidationMessage = validateBarcode(draft.barcode);
  const priceValidationMessage = validatePositivePriceField(draft.price);

  if (!draft.name.trim()) return 'Para guardar falta completar el nombre.';
  // SKU vacío OK: servicios lo autogenera productWriteRepo; físicos también permiten vacío.
  const base = skuValidationMessage ?? barcodeValidationMessage ?? priceValidationMessage;
  if (base) return base;

  if (opts.requireCategory && (!(draft.categoryId ?? '').trim() || isLegacyProductCategoryHint(draft.categoryId))) {
    return 'Para guardar falta elegir una categoría.';
  }

  if (
    opts.appointmentsEnabled &&
    draft.itemType === 'service' &&
    draft.isPublic &&
    draft.bookingMode !== 'quote_required'
  ) {
    const raw = draft.durationMinutes.trim();
    const n = Number(raw);
    if (!raw || !Number.isFinite(n) || n < 5) {
      return 'Para publicar un servicio con turnos, indicá la duración (mínimo 5 minutos).';
    }
  }
  return null;
}

// ─── Variant helpers ────────────────────────────────────────────────────────

const mapVariantToDraft = (
  variant: Awaited<ReturnType<typeof listVariantsForProduct>>[number],
  stockQty?: number | null,
): ProductVariantDraft => ({
  key: variant.id,
  id: variant.id,
  nameSuffix: variant.nameSuffix ?? '',
  sku: variant.sku ?? '',
  barcode: variant.barcode ?? '',
  price: variant.price != null ? String(variant.price) : '',
  attributes: { ...(variant.attributes ?? {}) },
  stockQty: stockQty != null ? String(stockQty) : '',
  imageUrl: variant.imageUrl ?? null,
});

const resetVariantEditor = (
  setVariantDrafts: (v: ProductVariantDraft[] | ((prev: ProductVariantDraft[]) => ProductVariantDraft[])) => void,
  setRemovedVariantIds: (v: string[] | ((prev: string[]) => string[])) => void,
) => {
  setVariantDrafts([]);
  setRemovedVariantIds([]);
};

const buildNewDraft = (_defaultCategorySlugForNewItem: string, defaultItemTypeForNew: ProductDraft['itemType']): ProductDraft => ({
  ...emptyDraft,
  categoryId: emptyDraft.categoryId,
  itemType: defaultItemTypeForNew,
});

const persistVariantDrafts = async (
  productId: string,
  branchId: string | null | undefined,
  drafts: ProductVariantDraft[],
  removedIds: string[],
) => {
  for (const variantId of removedIds) {
    // Zero out stock across ALL branches before deactivating.
    // This prevents orphaned stock rows that inflate the parent sum
    // and show phantom stock in the portal/POS.
    try {
      const stockRows = await findStockRowsForVariant(productId, variantId);
      for (const row of stockRows) {
        if (row.quantity > 0) {
          await adjustStock({
            stockId: row.id,
            delta: -row.quantity,
            reason: 'variant_deactivated',
          });
        }
      }
    } catch (error) {
      console.warn(`[catalogFormVm] No se pudo limpiar el stock de la variante ${variantId} antes de desactivar`, error);
    }
    await deactivateVariant(variantId);
  }
  for (const row of drafts) {
    // M5: Include price and stockQty in "has content" check so the user
    // doesn't silently lose a price or stock they entered on an otherwise empty row.
    const hasContent =
      row.nameSuffix.trim().length > 0 ||
      row.sku.trim().length > 0 ||
      row.barcode.trim().length > 0 ||
      Object.values(row.attributes).some((v) => v.trim().length > 0) ||
      (parseDecimalInput(row.price) != null && parseDecimalInput(row.price)! > 0) ||
      (parseDecimalInput(row.stockQty) != null && parseDecimalInput(row.stockQty)! > 0);
    if (!hasContent) continue;
    const attributes: Record<string, string> = {};
    for (const [k, v] of Object.entries(row.attributes)) {
      if (v.trim()) attributes[k] = v.trim();
    }
    const input = {
      productId,
      nameSuffix: row.nameSuffix.trim() || null,
      sku: row.sku.trim() || null,
      barcode: row.barcode.trim() || null,
      price: parseDecimalInput(row.price),
      attributes,
      imageUrl: row.imageUrl?.trim() || null,
    };
    let variantId = row.id;
    if (variantId) {
      await updateVariant(variantId, input);
    } else {
      variantId = await createVariant(input);
    }

    if (!branchId || !variantId) continue;

    // Opción A: Stock is read-only for existing variants — only set initial stock
    // for newly created variants. Existing variants manage stock from Inventory.
    if (row.id) continue;

    const targetQty = parseDecimalInput(row.stockQty);
    if (targetQty == null) continue;

    const currentRow = await findStockRow(productId, branchId, variantId);
    const currentQty = currentRow?.quantity ?? 0;
    const delta = targetQty - currentQty;
    if (delta === 0) continue;
    if (!currentRow && targetQty > 0) {
      const productName =
        getCatalogRamSnapshotCache()?.products.find((p) => String(p.id) === productId)?.name ?? '';
      await receiveStock({
        branchId,
        product: { id: productId, name: String(productName) },
        quantity: targetQty,
        unitCost: 0,
        minQuantity: 0,
        reason: 'stock_initial_load',
        variantId,
      });
    } else if (currentRow) {
      await adjustStock({
        stockId: currentRow.id,
        delta,
        reason: 'manual_adjustment',
      });
    }
  }
};

// ─── Plan limit ─────────────────────────────────────────────────────────────

async function assertWithinProductPlanLimitForForm(planEnt: PlanEntitlementsRow | null): Promise<boolean> {
  try {
    await assertWithinProductPlanLimit(planEnt);
    return true;
  } catch (error) {
    if (error instanceof CatalogPlanLimitExceededError) {
      const { title, message } = getCatalogPlanLimitExceededMessage(error);
      showError(title, message);
      return false;
    }
    throw error;
  }
}

// ─── Main view model ────────────────────────────────────────────────────────

type Params = {
  canEdit: boolean;
  user: { id: string; email?: string | null } | null;
  planEnt: PlanEntitlementsRow | null;
  planLoading: boolean;
  currentBranchId: string | null | undefined;
  loadFirstPage: () => Promise<void>;
  defaultCategorySlugForNewItem: string;
  defaultItemTypeForNew?: ProductDraft['itemType'];
  variantsEnabled?: boolean;
  refreshCatalogMasterOptions?: () => Promise<void>;
  getStockByProductAndBranch?: (branchId: string, productId: string) => Promise<StockRow>;
  // overlay state
  draft: ProductDraft;
  setDraft: (d: ProductDraft | ((prev: ProductDraft) => ProductDraft)) => void;
  editingProduct: CatalogProductRow | null;
  setEditingProduct: (p: CatalogProductRow | null) => void;
  selectedProduct: CatalogProductRow | null;
  setSelectedProduct: (p: CatalogProductRow | null) => void;
  saving: boolean;
  setSaving: (v: boolean) => void;
  modalVisible: boolean;
  setModalVisible: (v: boolean) => void;
  formMode: CatalogProductFormMode;
  setFormMode: (m: CatalogProductFormMode) => void;
  formSuggestion: RemoteHit | null;
  setFormSuggestion: (v: RemoteHit | null) => void;
  remoteSuggestion: RemoteHit | null;
  setRemoteSuggestion: (v: RemoteHit | null) => void;
  priceMarginPercentInput: string;
  setPriceMarginPercentInput: (s: string) => void;
  formCompact: boolean;
  setFormCompact: (v: boolean) => void;
  getCategoryLabel: (slug?: string | null) => string;
  categoryOptions: CategoryOption[];
  appointmentsEnabled?: boolean;
};

export function useCatalogFormVm({
  canEdit,
  user,
  planEnt,
  appointmentsEnabled = false,
  planLoading,
  currentBranchId,
  loadFirstPage,
  defaultCategorySlugForNewItem,
  defaultItemTypeForNew = 'physical',
  variantsEnabled = false,
  refreshCatalogMasterOptions,
  draft,
  setDraft,
  editingProduct,
  setEditingProduct,
  selectedProduct,
  setSelectedProduct,
  saving: _saving,
  setSaving,
  modalVisible: _modalVisible,
  setModalVisible,
  formMode: _formMode,
  setFormMode,
  formSuggestion: _formSuggestion,
  setFormSuggestion,
  remoteSuggestion,
  setRemoteSuggestion,
  priceMarginPercentInput,
  setPriceMarginPercentInput,
  formCompact,
  setFormCompact,
  getCategoryLabel,
  categoryOptions,
  getStockByProductAndBranch: getStockFn = async () => null,
}: Params) {
  // ── Variant drafts state ──
  const [variantDrafts, setVariantDrafts] = useState<ProductVariantDraft[]>([]);
  // Identidad de "sesión" del formulario: sube en cada apertura y en
  // "guardar y seguir" (nuevo borrador). El modal lo usa para resnapshotear
  // el baseline de detección de cambios sin guardar.
  const [formSession, setFormSession] = useState(0);
  const bumpFormSession = useCallback(() => setFormSession((n) => n + 1), []);
  const [removedVariantIds, setRemovedVariantIds] = useState<string[]>([]);
  // #705: variantes inactivas del producto en edición (para reactivar).
  const [inactiveVariants, setInactiveVariants] = useState<Awaited<ReturnType<typeof listInactiveVariantsForProduct>>>([]);
  const [imageUploading, setImageUploading] = useState(false);
  const [videoUploading, setVideoUploading] = useState(false);
  const [variantImageUploadingKey, setVariantImageUploadingKey] = useState<string | null>(null);
  const [axisImageUploading, setAxisImageUploading] = useState<string | null>(null);
  const [comboDraftComponents, setComboDraftComponents] = useState<ComboDraftComponent[]>([]);

  // ── Validation ──
  const skuValidationMessage = validateSku(draft.sku);
  const barcodeValidationMessage = validateBarcode(draft.barcode);
  const codeValidationMessage = skuValidationMessage;
  const priceValidationMessage = validatePositivePriceField(draft.price);
  const draftValidationMessage = buildProductDraftValidationMessage(draft, {
    appointmentsEnabled,
    requireCategory: Boolean(_formSuggestion) || isLegacyProductCategoryHint(draft.categoryId),
  });
  const isDraftReady = !draftValidationMessage;
  const normalizedDraftPrice = parseDecimalInput(draft.price) ?? 0;

  // ── Modal open/close ──
  const closeModal = () => {
    // Only hide. Don't wipe draft/formMode/formCompact here — RN Modal still
    // paints children during fade/slide, so resetting flashed defaults (e.g.
    // Hora → Unidad, compact → rail layout mid-fade).
    // openCreate / openEdit / openDuplicate always load a fresh draft, and
    // openCreate siempre setea formCompact explícito.
    setModalVisible(false);
    setFormSuggestion(null);
  };

  const openCreateModal = async (opts?: { itemType?: ProductDraft['itemType']; compact?: boolean }) => {
    if (!canEdit) {
      showInfo('No podés hacer cambios', 'Podés ver productos, pero no agregar.');
      return;
    }
    if (planLoading) {
      showError('Esperá un momento', 'Estamos cargando los límites de tu plan. Intentá de nuevo en unos segundos.');
      return;
    }
    if (!(await assertWithinProductPlanLimitForForm(planEnt))) return;
    await refreshCatalogMasterOptions?.();
    setEditingProduct(null);
    setFormMode('create');
    let compact = opts?.compact === true;
    if (opts?.compact === undefined) {
      const orgId = planEnt?.organization_id ?? null;
      if (orgId) {
        const layout = await readCatalogCreateFormLayout(orgId);
        compact = layout === 'express';
      } else {
        compact = true;
      }
    }
    setFormCompact(compact);
    setDraft(buildNewDraft(defaultCategorySlugForNewItem, opts?.itemType ?? defaultItemTypeForNew));
    setComboDraftComponents([]);
    resetVariantEditor(setVariantDrafts, setRemovedVariantIds);
    setPriceMarginPercentInput('35');
    bumpFormSession();
    setModalVisible(true);
  };

  const openDuplicateModal = async (product: CatalogProductRow) => {
    if (!canEdit) {
      showInfo('No podés hacer cambios', 'Podés ver productos, pero no duplicarlos.');
      return;
    }
    if (product.isMisc) {
      showInfo('Línea interna', 'Varios es interno del mostrador. No se duplica.');
      return;
    }
    await refreshCatalogMasterOptions?.();
    const stored = await getProductById(product.id);
    const taxClass = stored && 'taxClass' in stored ? (stored as { taxClass?: string | null }).taxClass : null;
    setEditingProduct(null);
    setFormMode('duplicate');
    setDraft({
      name: duplicateProductDraftName(product.name),
      barcode: '',
      sku: '',
      price: String(product.price),
      categoryId: product.categoryId,
      itemType: product.itemType === 'service' ? 'service' : 'physical',
      brandId: product.brandId ?? '',
      internalNotes: product.internalNotes ?? '',
      publicDescription: stored?.publicDescription ?? '',
      minOrderQty:
        stored?.minOrderQty != null && Number.isFinite(stored.minOrderQty)
          ? String(stored.minOrderQty)
          : '',
      maxOrderQty:
        stored?.maxOrderQty != null && Number.isFinite(stored.maxOrderQty)
          ? String(stored.maxOrderQty)
          : '',
      isPublic: stored?.isPublic ?? true,
      menuAvailable: stored?.menuAvailable ?? true,
      visibleOnMenuCarta: stored?.visibleOnMenuCarta ?? true,
      activeIngredient: stored?.activeIngredient ?? '',
      usageTags: Array.isArray(stored?.usageTags) ? (stored!.usageTags as string[]).join(', ') : '',
      allergens: Array.isArray(stored?.allergens) ? [...(stored!.allergens as string[])] : [],
      preferredSupplierId: product.preferredSupplierId ?? '',
      supplierItemCode: product.supplierItemCode ?? '',
      ivaRate: taxClassToIvaRate(taxClass),
      // Prefer SQLite/stored: list/cache rows can omit unit_of_measure after create.
      unitOfMeasure: stored?.unitOfMeasure ?? product.unitOfMeasure ?? 'unit',
      sellByWeight: stored?.sellByWeight ?? product.sellByWeight ?? false,
      durationMinutes:
        stored?.durationMinutes != null && Number.isFinite(stored.durationMinutes)
          ? String(stored.durationMinutes)
          : '',
      bufferBeforeMinutes:
        stored?.bufferBeforeMinutes != null && Number.isFinite(stored.bufferBeforeMinutes)
          ? String(stored.bufferBeforeMinutes)
          : '',
      bufferAfterMinutes:
        stored?.bufferAfterMinutes != null && Number.isFinite(stored.bufferAfterMinutes)
          ? String(stored.bufferAfterMinutes)
          : '',
      imageUrl: stored?.imageUrl ?? '',
      videoUrl: stored?.videoUrl ?? '',
      bookingMode: stored?.bookingMode === 'quote_required' ? 'quote_required' : 'direct',
      evaluationProductId: stored?.evaluationProductId ?? '',
      bookingPaymentMode:
        stored?.bookingPaymentMode === 'none' ||
        stored?.bookingPaymentMode === 'deposit' ||
        stored?.bookingPaymentMode === 'full'
          ? stored.bookingPaymentMode
          : 'inherit',
      bookingDepositAmount:
        stored?.bookingDepositAmount != null ? String(stored.bookingDepositAmount) : '',
      bookingDepositPercent:
        stored?.bookingDepositPercent != null ? String(stored.bookingDepositPercent) : '',
      kitchenDestination: stored?.kitchenDestination ?? 'kitchen',
      prepMinutes:
        stored?.prepMinutes != null && Number.isFinite(stored.prepMinutes)
          ? String(stored.prepMinutes)
          : '',
    });
    // #712: clonar la matriz de variantes del producto original.
    // Los IDs se resetean (se generan nuevos al guardar); stock arranca en 0.
    if (variantsEnabled) {
      try {
        const variants = await listVariantsForProduct(product.id);
        if (variants.length > 0) {
          const clonedDrafts = variants.map((v) => ({
            key: crypto.randomUUID(),
            id: undefined,
            nameSuffix: v.nameSuffix ?? '',
            sku: '', // SKU se resetea — no duplicar SKUs únicos
            barcode: '', // barcode se reseta — no duplicar barcodes únicos
            price: v.price != null ? String(v.price) : '',
            attributes: { ...(v.attributes ?? {}) },
            stockQty: '', // stock arranca vacío para un producto nuevo
            imageUrl: v.imageUrl ?? null,
          }));
          setVariantDrafts(clonedDrafts);
        } else {
          resetVariantEditor(setVariantDrafts, setRemovedVariantIds);
        }
      } catch {
        resetVariantEditor(setVariantDrafts, setRemovedVariantIds);
      }
    } else {
      resetVariantEditor(setVariantDrafts, setRemovedVariantIds);
    }
    setInactiveVariants([]);
    setRemovedVariantIds([]);
    setFormSuggestion(null);
    setPriceMarginPercentInput('35');
    bumpFormSession();
    setModalVisible(true);
  };

  const openEditModal = async (product: CatalogProductRow) => {
    if (!canEdit) {
      showInfo('No podés hacer cambios', 'Podés ver productos, pero no editar.');
      return;
    }
    if (product.isMisc) {
      showInfo('Línea interna', 'Varios es interno del mostrador. No se edita.');
      return;
    }
    // El "Encontramos…" del scan deja de aplicar al pasar a editar.
    useCatalogOverlayStore.getState().setLookupFeedback(null);
    await refreshCatalogMasterOptions?.();
    const stored = await getProductById(product.id);
    const taxClass = stored && 'taxClass' in stored ? (stored as { taxClass?: string | null }).taxClass : null;
    setEditingProduct(product);
    setFormMode('edit');
    const productBarcode = product.barcode != null && String(product.barcode).trim() !== '' ? String(product.barcode).trim() : '';
    const productSku = product.sku != null ? String(product.sku).trim() : '';
    setDraft({
      name: product.name,
      barcode: productBarcode,
      sku: product.sku,
      price: String(product.price),
      categoryId: product.categoryId,
      itemType: product.itemType === 'service' ? 'service' : 'physical',
      brandId: product.brandId ?? '',
      internalNotes: product.internalNotes ?? '',
      publicDescription: stored?.publicDescription ?? '',
      minOrderQty:
        stored?.minOrderQty != null && Number.isFinite(stored.minOrderQty)
          ? String(stored.minOrderQty)
          : '',
      maxOrderQty:
        stored?.maxOrderQty != null && Number.isFinite(stored.maxOrderQty)
          ? String(stored.maxOrderQty)
          : '',
      isPublic: stored?.isPublic ?? true,
      menuAvailable: stored?.menuAvailable ?? true,
      visibleOnMenuCarta: stored?.visibleOnMenuCarta ?? true,
      activeIngredient: stored?.activeIngredient ?? '',
      usageTags: Array.isArray(stored?.usageTags) ? (stored!.usageTags as string[]).join(', ') : '',
      allergens: Array.isArray(stored?.allergens) ? [...(stored!.allergens as string[])] : [],
      preferredSupplierId: product.preferredSupplierId ?? '',
      supplierItemCode: product.supplierItemCode ?? '',
      ivaRate: taxClassToIvaRate(taxClass),
      // Prefer SQLite/stored: list/cache rows can omit unit_of_measure after create.
      unitOfMeasure: stored?.unitOfMeasure ?? product.unitOfMeasure ?? 'unit',
      sellByWeight: stored?.sellByWeight ?? product.sellByWeight ?? false,
      durationMinutes:
        stored?.durationMinutes != null && Number.isFinite(stored.durationMinutes)
          ? String(stored.durationMinutes)
          : '',
      bufferBeforeMinutes:
        stored?.bufferBeforeMinutes != null && Number.isFinite(stored.bufferBeforeMinutes)
          ? String(stored.bufferBeforeMinutes)
          : '',
      bufferAfterMinutes:
        stored?.bufferAfterMinutes != null && Number.isFinite(stored.bufferAfterMinutes)
          ? String(stored.bufferAfterMinutes)
          : '',
      imageUrl: stored?.imageUrl ?? '',
      videoUrl: stored?.videoUrl ?? '',
      bookingMode: stored?.bookingMode === 'quote_required' ? 'quote_required' : 'direct',
      evaluationProductId: stored?.evaluationProductId ?? '',
      bookingPaymentMode:
        stored?.bookingPaymentMode === 'none' ||
        stored?.bookingPaymentMode === 'deposit' ||
        stored?.bookingPaymentMode === 'full'
          ? stored.bookingPaymentMode
          : 'inherit',
      bookingDepositAmount:
        stored?.bookingDepositAmount != null ? String(stored.bookingDepositAmount) : '',
      bookingDepositPercent:
        stored?.bookingDepositPercent != null ? String(stored.bookingDepositPercent) : '',
      kitchenDestination: stored?.kitchenDestination ?? 'kitchen',
      prepMinutes:
        stored?.prepMinutes != null && Number.isFinite(stored.prepMinutes)
          ? String(stored.prepMinutes)
          : '',
    });
    if (variantsEnabled) {
      try {
        const variants = await listVariantsForProduct(product.id);
        if (currentBranchId) {
          const drafts = await Promise.all(
            variants.map(async (variant) => {
              const row = await findStockRow(product.id, currentBranchId, variant.id);
              return mapVariantToDraft(variant, row?.quantity ?? 0);
            }),
          );
          setVariantDrafts(drafts);
        } else {
          setVariantDrafts(variants.map((v) => mapVariantToDraft(v)));
        }
        // #705: cargar variantes inactivas para mostrar sección de reactivación.
        try {
          const inactive = await listInactiveVariantsForProduct(product.id);
          setInactiveVariants(inactive);
        } catch {
          setInactiveVariants([]);
        }
      } catch {
        setVariantDrafts([]);
        setInactiveVariants([]);
      }
      setRemovedVariantIds([]);
    } else {
      resetVariantEditor(setVariantDrafts, setRemovedVariantIds);
      setInactiveVariants([]);
    }
    setPriceMarginPercentInput('35');
    const overlay = useCatalogOverlayStore.getState();
    const detailTab = overlay.selectedProductDetailTab;
    if (detailTab === 'composicion') {
      overlay.requestFormRailSection('composition');
    } else if (detailTab === 'receta') {
      overlay.requestFormRailSection('receta');
    }
    bumpFormSession();
    setModalVisible(true);
  };

  // ── Save ──
  const handleSaveInternal = async ({ continueCreating = false }: { continueCreating?: boolean } = {}) => {
    try {
      if (planLoading) {
        showError('Esperá un momento', 'Estamos cargando los límites de tu plan. Intentá de nuevo en unos segundos.');
        return;
      }
      if (
        (Boolean(_formSuggestion) || isLegacyProductCategoryHint(draft.categoryId)) &&
        (!(draft.categoryId ?? '').trim() || isLegacyProductCategoryHint(draft.categoryId))
      ) {
        showError('Falta la categoría', 'Elegí una categoría de la lista.');
        return;
      }
      setSaving(true);
      const payload = {
        name: draft.name,
        sku: draft.sku,
        barcode: draft.barcode.trim().length > 0 ? draft.barcode : undefined,
        price: parseDecimalInput(draft.price) ?? 0,
        categoryId: draft.categoryId,
        itemType: draft.itemType,
        brandId: draft.brandId.trim().length > 0 ? draft.brandId.trim() : null,
        internalNotes: draft.internalNotes.trim().length > 0 ? draft.internalNotes.trim() : null,
        publicDescription: draft.publicDescription.trim().length > 0 ? draft.publicDescription.trim() : null,
        minOrderQty: draft.isPublic ? draft.minOrderQty.trim() || null : null,
        maxOrderQty: draft.isPublic ? draft.maxOrderQty.trim() || null : null,
        preferredSupplierId: draft.preferredSupplierId.trim().length > 0 ? draft.preferredSupplierId.trim() : null,
        supplierItemCode: draft.supplierItemCode.trim().length > 0 ? draft.supplierItemCode.trim() : null,
        taxClass: ivaRateToTaxClass(draft.ivaRate),
        unitOfMeasure: draft.unitOfMeasure ?? 'unit',
        sellByWeight:
          draft.unitOfMeasure === 'kg' || draft.unitOfMeasure === 'g',
        durationMinutes: (() => {
          const raw = draft.durationMinutes.trim();
          if (!raw) return null;
          const n = Math.floor(Number(raw));
          return Number.isFinite(n) && n > 0 ? n : null;
        })(),
        bufferBeforeMinutes: (() => {
          if (draft.itemType !== 'service') return 0;
          const raw = draft.bufferBeforeMinutes.trim();
          if (!raw) return 0;
          const n = Math.floor(Number(raw));
          return Number.isFinite(n) && n >= 0 ? Math.min(n, 180) : 0;
        })(),
        bufferAfterMinutes: (() => {
          if (draft.itemType !== 'service') return 0;
          const raw = draft.bufferAfterMinutes.trim();
          if (!raw) return 0;
          const n = Math.floor(Number(raw));
          return Number.isFinite(n) && n >= 0 ? Math.min(n, 180) : 0;
        })(),
        imageUrl: draft.imageUrl.trim().length > 0 ? draft.imageUrl.trim() : null,
        videoUrl: (draft.videoUrl ?? '').trim().length > 0 ? (draft.videoUrl ?? '').trim() : null,
        isPublic: draft.isPublic,
        activeIngredient: draft.activeIngredient.trim() || null,
        usageTags: draft.usageTags.split(',').map((t) => t.trim()).filter(Boolean),
        allergens: (draft.allergens ?? []).map((t) => String(t).trim()).filter(Boolean),
        bookingMode: draft.itemType === 'service' ? draft.bookingMode : 'direct',
        evaluationProductId:
          draft.itemType === 'service' && draft.bookingMode === 'quote_required'
            ? draft.evaluationProductId.trim() || null
            : null,
        bookingPaymentMode: draft.itemType === 'service' ? draft.bookingPaymentMode : 'inherit',
        bookingDepositAmount: (() => {
          if (draft.itemType !== 'service' || draft.bookingPaymentMode !== 'deposit') return null;
          const n = parseDecimalInput(draft.bookingDepositAmount);
          return n != null && n >= 0 ? n : null;
        })(),
        bookingDepositPercent: (() => {
          if (draft.itemType !== 'service' || draft.bookingPaymentMode !== 'deposit') return null;
          const n = parseDecimalInput(draft.bookingDepositPercent);
          return n != null && n >= 0 && n <= 100 ? n : null;
        })(),
        kitchenDestination: draft.kitchenDestination,
        prepMinutes: (() => {
          const raw = draft.prepMinutes.trim();
          if (!raw) return null;
          const n = Math.floor(Number(raw));
          return Number.isFinite(n) && n >= 5 && n <= 240 ? n : null;
        })(),
        menuAvailable: draft.menuAvailable !== false,
        visibleOnMenuCarta: isCategoryRestricted ? false : draft.visibleOnMenuCarta !== false,
      };

      const wasEditing = Boolean(editingProduct);
      let productId = editingProduct?.id ?? '';

      if (editingProduct) {
        await updateProduct(editingProduct.id, payload, { performedByUserId: user?.id ?? null });
      } else {
        if (!(await assertWithinProductPlanLimitForForm(planEnt))) {
          setSaving(false);
          return;
        }
        const comboParts = comboDraftComponents.filter((c) => c.productId?.trim());
        if (comboParts.length > 0) {
          const comboErr = validateComboDraft('00000000-0000-4000-8000-000000000001', comboParts);
          if (comboErr) {
            showError('Composición inválida', 'Revisá las partes del combo antes de guardar.');
            setSaving(false);
            return;
          }
        }
        productId = await createProduct(payload, {
          performedByUserId: user?.id ?? null,
          initialBranchQuantity: comboParts.length > 0 ? 0 : undefined,
        });
        if (comboParts.length > 0 && productId) {
          await upsertProductCombo(
            productId,
            comboParts.map((c, i) => ({ productId: c.productId, qty: c.qty, sortOrder: i + 1 })),
          );
          invalidateComboParentProductIdsCache();
        }
      }

      if (variantsEnabled && productId && draft.itemType === 'physical') {
        await persistVariantDrafts(productId, currentBranchId, variantDrafts, removedVariantIds);
      }

      const createdWithCombo = !wasEditing && comboDraftComponents.some((c) => c.productId?.trim());

      if (continueCreating && !wasEditing) {
        setEditingProduct(null);
        setFormMode('create');
        setDraft(buildNewDraft(defaultCategorySlugForNewItem, defaultItemTypeForNew));
        setComboDraftComponents([]);
        resetVariantEditor(setVariantDrafts, setRemovedVariantIds);
        setFormSuggestion(null);
        setPriceMarginPercentInput('35');
        bumpFormSession();
      } else if (createdWithCombo && productId) {
        closeModal();
        requestCatalogProductDetail(productId, { tab: 'resumen', hubSegment: 'products' });
      } else {
        closeModal();
      }
      await loadFirstPage();
      const noun = catalogItemNoun(draft.itemType);
      if (createdWithCombo && !continueCreating) {
        showSuccess('Combo guardado', 'Las partes ya están armadas.');
      } else {
        showSuccess(
          `${noun.Singular} guardado`,
          continueCreating && !wasEditing
            ? `${noun.Singular} agregado. Ya podés cargar el siguiente.`
            : wasEditing
              ? 'Los cambios se guardaron.'
              : `${noun.Singular} agregado.`,
        );
      }
      if (!wasEditing) {
        hapticSuccess();
        if (formCompact) {
          // Funnel (#1362): el alta "Carga rápida" es el paso day1 de más
          // abandono potencial — lo medimos explícito.
          trackOnboardingFunnel('catalog_product_created', { mode: 'compact' });
        }
      }
    } catch (error: unknown) {
      if (error instanceof CatalogDomainError) {
        showError('No pudimos guardar', error.message);
      } else if (error instanceof Error && error.message.includes('stale_variant_update')) {
        // H2/H3: Optimistic concurrency rejection — another device edited the variant.
        showError(
          'Variante modificada en otro dispositivo',
          'Alguien más editó esta variante mientras la estabas cambiando. Recargá el catálogo e intentá de nuevo.',
        );
      } else {
        presentUnexpectedFailure({ module: 'catalog', op: 'product_form.save', error, userMessage: 'No se pudo guardar el producto.' });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => handleSaveInternal();
  const handleSaveAndContinue = () => handleSaveInternal({ continueCreating: true });

  // ── Variant draft operations ──
  const addVariantDraft = () => setVariantDrafts((current) => [...current, emptyVariantDraft()]);
  const replaceVariantDrafts = (drafts: ProductVariantDraft[]) => setVariantDrafts(drafts);
  const generateVariantMatrix = (axisInputs: AxisInput[]) => {
    const axes = axisInputs
      .filter((a) => a.name.trim() && a.values.trim())
      .map((a) => ({
        name: a.name.trim().toLowerCase(),
        values: parseAxisValues(a.values),
      }));
    if (axes.length === 0) {
      showInfo('Completá los atributos', 'Ingresá al menos un atributo con valores para generar las combinaciones.');
      return;
    }
    const merged = buildVariantMatrixDrafts(variantDrafts, axes, draft.price);
    const stamped = merged.map((row, index) => {
      if (row.barcode.trim()) return row;
      const attrSeed = Object.values(row.attributes).join('-');
      const seed = `${draft.sku || draft.name || 'variant'}-${index}-${attrSeed}`;
      return { ...row, barcode: suggestInternalVariantBarcode(seed) };
    });
    setVariantDrafts(stamped);
    const added = stamped.length - variantDrafts.length;
    const preserved = variantDrafts.length;
    showSuccess(
      'Combinaciones generadas',
      added > 0
        ? `${stamped.length} variantes en total: ${added} nuevas${preserved > 0 ? `, ${preserved} conservadas con sus valores` : ''}.`
        : `${stamped.length} variantes. Se conservaron los valores ya cargados.`,
    );
  };
  const updateVariantDraft = (key: string, patch: Partial<ProductVariantDraft>) => {
    setVariantDrafts((current) => current.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  };
  const removeVariantDraft = (key: string) => {
    setVariantDrafts((current) => {
      const target = current.find((row) => row.key === key);
      if (target?.id) {
        setRemovedVariantIds((ids) => (ids.includes(target.id!) ? ids : [...ids, target.id!]));
      }
      return current.filter((row) => row.key !== key);
    });
  };

  // #705: reactivar una variante previamente desactivada.
  // La mueve de inactiveVariants a variantDrafts (como draft editable).
  const handleReactivateVariant = useCallback(async (variantId: string) => {
    try {
      await reactivateVariant(variantId);
      const reactivated = inactiveVariants.find((v) => v.id === variantId);
      if (!reactivated) return;
      // Mover a drafts activos con stock 0 (el stock existente se preserva en backend).
      const draft = mapVariantToDraft(reactivated, 0);
      setVariantDrafts((prev) => [...prev, draft]);
      setInactiveVariants((prev) => prev.filter((v) => v.id !== variantId));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No se pudo reactivar la variante.';
      showInfo('Error', message);
    }
  }, [inactiveVariants, showInfo]);

  // ── Detail pane ──
  const openProductDetail = useCallback((
    product: CatalogProductRow,
    opts?: { tab?: CatalogProductDetailTab; recipeKind?: 'assemble' | 'disassemble' },
  ) => {
    useCatalogOverlayStore.getState().setLookupFeedback(null);
    setSelectedProduct(product);
    useCatalogOverlayStore.getState().setSelectedProductDetailTab(opts?.tab ?? 'resumen');
    if (opts?.recipeKind) {
      useCatalogOverlayStore.getState().setSelectedProductRecipeKind(opts.recipeKind);
    }
    const localSku = product.sku != null ? String(product.sku).trim() : '';
    const localBarcode = product.barcode != null ? String(product.barcode).trim() : '';
    const needsServerPatch =
      product.itemType !== 'service' &&
      (!localSku ||
        (localBarcode !== '' &&
          localSku !== '' &&
          localBarcode.toUpperCase() === localSku.toUpperCase()));
    if (needsServerPatch) {
      void patchLocalProductSkuFromServer(product.id).then((sku) => {
        if (!sku) return;
        const current = useCatalogOverlayStore.getState().selectedProduct;
        if (current?.id !== product.id) return;
        if (trimProductSku(current.sku)) return;
        setSelectedProduct({ ...current, sku });
      });
    }
  }, [setSelectedProduct]);

  const closeProductDetail = useCallback(() => {
    setSelectedProduct(null);
    useCatalogOverlayStore.getState().setSelectedProductDetailTab('resumen');
    useCatalogOverlayStore.getState().setSelectedProductRecipeKind('assemble');
  }, [setSelectedProduct]);

  const {
    stock: selectedProductStock,
    loading: selectedProductStockLoading,
    failed: selectedProductStockFailed,
    retry: retrySelectedProductStock,
  } = useProductBranchStock({
    branchId: currentBranchId ?? null,
    productId:
      selectedProduct?.itemType === 'service' || selectedProduct?.isCombo === true
        ? null
        : selectedProduct?.id ?? null,
    warnLabel: 'del producto físico',
    getStockFn,
  });

  const {
    stock: editingProductStock,
    loading: editingProductStockLoading,
    failed: editingProductStockFailed,
    retry: retryEditingProductStock,
  } = useProductBranchStock({
    branchId: currentBranchId ?? null,
    productId:
      editingProduct?.itemType === 'service' || editingProduct?.isCombo === true
        ? null
        : editingProduct?.id ?? null,
    warnLabel: 'para edición',
    getStockFn,
  });

  useEffect(() => {
    let cancelled = false;
    const product = selectedProduct;
    const branchId = currentBranchId;
    if (!product || !branchId || product.itemType === 'service') {
      return;
    }
    void fetchCatalogComboMetrics(branchId, [product.id]).then((map) => {
      if (cancelled) return;
      const m = map[product.id];
      if (!m) return;
      const current = useCatalogOverlayStore.getState().selectedProduct;
      if (current?.id !== product.id) return;
      setSelectedProduct({
        ...current,
        isCombo: true,
        comboReferenceUnitCost: m.referenceUnitCost,
        comboReferenceComplete: m.referenceComplete,
        comboSellableQty: m.sellableQty,
      });
    });
    return () => {
      cancelled = true;
    };
  }, [selectedProduct?.id, selectedProduct?.isCombo, currentBranchId, setSelectedProduct]);

  const selectedCategoryLabel = selectedProduct ? getCategoryLabel(selectedProduct.categoryId) : '';
  // Costo 0 = nunca se cargó costo: no es un costo válido para margen/sugerencia.
  const selectedProductAvgCost = selectedProduct?.isCombo
    ? selectedProduct.comboReferenceUnitCost != null && selectedProduct.comboReferenceUnitCost > 0
      ? selectedProduct.comboReferenceUnitCost
      : null
    : selectedProductStock != null && selectedProductStock.avgCost > 0
      ? selectedProductStock.avgCost
      : null;
  const selectedProductMarginValue = selectedProduct
    ? calculateMarginValue(selectedProduct.price, selectedProductAvgCost)
    : null;
  const selectedProductMarginPercent = selectedProduct
    ? calculateMarginPercent(selectedProduct.price, selectedProductMarginValue)
    : null;

  const editingProductCost = editingProduct?.isCombo
    ? editingProduct.comboReferenceUnitCost != null && editingProduct.comboReferenceUnitCost > 0
      ? editingProduct.comboReferenceUnitCost
      : null
    : editingProductStock != null && editingProductStock.avgCost > 0
      ? editingProductStock.avgCost
      : null;
  const editingProductCurrentMarginValue = calculateMarginValue(normalizedDraftPrice, editingProductCost);
  const editingProductCurrentMarginPercent = calculateMarginPercent(normalizedDraftPrice, editingProductCurrentMarginValue);
  const editingProductSuggestedPrice = calculateSuggestedPrice(editingProductCost, priceMarginPercentInput);

  const applySuggestedPrice = useCallback(() => {
    if (editingProductSuggestedPrice == null) return;
    // Mantiene centavos — no redondear a entero.
    setDraft((current) => ({ ...current, price: formatEditableMoneyAmount(editingProductSuggestedPrice) }));
  }, [editingProductSuggestedPrice, setDraft]);

  const useRemoteSuggestion = useCallback(() => {
    if (!remoteSuggestion) return;
    setEditingProduct(null);
    setDraft({
      ...emptyDraft,
      ...buildProductDraftFromSuggestion({
        barcode: remoteSuggestion.barcode,
        suggestion: remoteSuggestion.suggestion,
      }),
    });
    setFormSuggestion(remoteSuggestion);
    bumpFormSession();
    setModalVisible(true);
  }, [remoteSuggestion, setDraft, setEditingProduct, setFormSuggestion, setModalVisible, bumpFormSession]);

  const dismissRemoteSuggestion = useCallback(() => {
    setRemoteSuggestion(null);
  }, [setRemoteSuggestion]);

  // ── Image handlers ──
  const handlePickImage = useCallback(async () => {
    try {
      const picked = await pickProductImage();
      if (!picked) return;

      setImageUploading(true);

      let productId = editingProduct?.id ?? '';
      if (!productId) {
        productId = crypto.randomUUID();
      }

      const orgId = await requireSessionOrganizationId();
      const optimized = await optimizeProductImage(picked.uri);
      const publicUrl = await uploadProductImage({
        orgId,
        productId,
        optimized,
      });

      setDraft((prev) => ({ ...prev, imageUrl: publicUrl }));
    } catch (err) {
      if (isProductImageValidationError(err)) {
        showError('Imagen no válida', err.message);
      } else {
        presentUnexpectedFailure({
          module: 'catalog',
          op: 'product.image_upload',
          error: err,
          title: 'No se pudo subir la imagen',
          userMessage: 'No pudimos subir la imagen. Reintentá en un momento.',
        });
      }
    } finally {
      setImageUploading(false);
    }
  }, [editingProduct?.id, setDraft]);

  const handleRemoveImage = useCallback(async () => {
    const prevUrl = draft.imageUrl;
    setDraft((prev) => ({ ...prev, imageUrl: '' }));

    if (editingProduct?.id && prevUrl) {
      try {
        const orgId = await requireSessionOrganizationId();
        await removeProductImage({ orgId, productId: editingProduct.id });
      } catch (err) {
        console.warn('[catalogFormVm] No se pudo borrar la imagen del bucket', err);
      }
    }
  }, [draft.imageUrl, editingProduct?.id, setDraft]);

  // ── Video (reel) handlers — bucket product-media; el detalle de tienda lo muestra autoplay muted ──
  const handlePickVideo = useCallback(async () => {
    try {
      const picked = await pickProductVideo();
      if (!picked) return;

      setVideoUploading(true);
      const productId = editingProduct?.id ?? crypto.randomUUID();
      const orgId = await requireSessionOrganizationId();
      const publicUrl = await uploadProductVideo({
        orgId,
        productId,
        base64: picked.base64,
        mimeType: picked.mimeType,
      });
      setDraft((prev) => ({ ...prev, videoUrl: publicUrl }));
    } catch (err) {
      if (isProductVideoValidationError(err)) {
        showError('Video no válido', err.message);
      } else {
        presentUnexpectedFailure({
          module: 'catalog',
          op: 'product.video_upload',
          error: err,
          title: 'No se pudo subir el video',
          userMessage: 'No pudimos subir el video. Reintentá en un momento.',
        });
      }
    } finally {
      setVideoUploading(false);
    }
  }, [editingProduct?.id, setDraft]);

  const handleRemoveVideo = useCallback(async () => {
    const prevUrl = draft.videoUrl;
    setDraft((prev) => ({ ...prev, videoUrl: '' }));

    if (editingProduct?.id && prevUrl) {
      try {
        const orgId = await requireSessionOrganizationId();
        await removeProductVideo({ orgId, productId: editingProduct.id });
      } catch (err) {
        console.warn('[catalogFormVm] No se pudo borrar el video del bucket', err);
      }
    }
  }, [draft.videoUrl, editingProduct?.id, setDraft]);

  // ── Variant image handlers ──
  const handlePickVariantImage = useCallback(async (variantKey: string) => {
    const vd = variantDrafts.find((d) => d.key === variantKey);
    if (!vd) return;
    try {
      const picked = await pickProductImage();
      if (!picked) return;

      setVariantImageUploadingKey(variantKey);

      let variantId = vd.id ?? '';
      if (!variantId) {
        variantId = crypto.randomUUID();
        setVariantDrafts((prev) =>
          prev.map((d) => (d.key === variantKey ? { ...d, id: variantId } : d)),
        );
      }

      const orgId = await requireSessionOrganizationId();
      const optimized = await optimizeProductImage(picked.uri);
      const publicUrl = await uploadProductVariantImage({
        orgId,
        variantId,
        optimized,
      });

      setVariantDrafts((prev) =>
        prev.map((d) => (d.key === variantKey ? { ...d, imageUrl: publicUrl } : d)),
      );
    } catch (err) {
      if (isProductImageValidationError(err)) {
        showError('Imagen no válida', err.message);
      } else {
        presentUnexpectedFailure({
          module: 'catalog',
          op: 'variant.image_upload',
          error: err,
          title: 'No se pudo subir la imagen',
          userMessage: 'No pudimos subir la imagen de la variante. Reintentá en un momento.',
        });
      }
    } finally {
      setVariantImageUploadingKey(null);
    }
  }, [variantDrafts]);

  const handleRemoveVariantImage = useCallback(async (variantKey: string) => {
    const vd = variantDrafts.find((d) => d.key === variantKey);
    if (!vd) return;
    const prevUrl = vd.imageUrl ?? null;
    setVariantDrafts((prev) =>
      prev.map((d) => (d.key === variantKey ? { ...d, imageUrl: null } : d)),
    );

    if (vd.id && prevUrl) {
      try {
        const orgId = await requireSessionOrganizationId();
        await removeProductVariantImage({ orgId, variantId: vd.id });
      } catch (err) {
        console.warn('[catalogFormVm] No se pudo borrar la imagen de la variante del bucket', err);
      }
    }
  }, [variantDrafts]);

  // ── Axis image handlers (propagate to all variants with same value) ──
  const handlePickAxisImage = useCallback(async (axis: string, value: string) => {
    if (!value.trim()) return;
    const uploadKey = `${axis}:${value}`;
    try {
      const picked = await pickProductImage();
      if (!picked) return;

      setAxisImageUploading(uploadKey);

      let productId = editingProduct?.id ?? '';
      if (!productId) {
        productId = crypto.randomUUID();
      }

      const orgId = await requireSessionOrganizationId();
      const optimized = await optimizeProductImage(picked.uri);
      const publicUrl = await uploadProductAxisImage({
        orgId,
        productId,
        axis,
        value,
        optimized,
      });

      // Propagate to all variant drafts with this axis value.
      // M6: Don't overwrite a variant-specific image that the user already set
      // for this particular variant — the axis image is a fallback, not a force-override.
      setVariantDrafts((prev) =>
        prev.map((d) =>
          d.attributes?.[axis] === value && !d.imageUrl ? { ...d, imageUrl: publicUrl } : d,
        ),
      );
    } catch (err) {
      if (isProductImageValidationError(err)) {
        showError('Imagen no válida', err.message);
      } else {
        presentUnexpectedFailure({
          module: 'catalog',
          op: 'axis.image_upload',
          error: err,
          title: 'No se pudo subir la imagen',
          userMessage: 'No pudimos subir la imagen. Reintentá en un momento.',
        });
      }
    } finally {
      setAxisImageUploading(null);
    }
  }, [editingProduct?.id]);

  const handleRemoveAxisImage = useCallback(async (axis: string, value: string) => {
    if (!value.trim()) return;
    // Clear imageUrl on all variant drafts with this axis value.
    setVariantDrafts((prev) =>
      prev.map((d) =>
        d.attributes?.[axis] === value ? { ...d, imageUrl: null } : d,
      ),
    );

    if (editingProduct?.id) {
      try {
        const orgId = await requireSessionOrganizationId();
        await removeProductAxisImage({ orgId, productId: editingProduct.id, axis, value });
      } catch (err) {
        console.warn('[catalogFormVm] No se pudo borrar la imagen del eje del bucket', err);
      }
    }
  }, [editingProduct?.id]);

  /**
   * Estado de verificación de stock para el confirm de desactivar.
   * - Filas de la lista: `stockQuantity` viene de la lectura del snapshot —
   *   `null` ahí significa "sin registro de stock" (verificado en 0), no error.
   * - Producto seleccionado: lectura dedicada con estados checking/unverified
   *   y `retry` para volver a intentar antes de desactivar.
   */
  const getProductStockCheck = useCallback(
    (product: CatalogProductRow): { check: CatalogStockCheckResult; retry: () => void } => {
      if (product.itemType === 'service') {
        return { check: { state: 'verified', quantity: 0 }, retry: () => {} };
      }
      if (selectedProduct?.id === product.id && currentBranchId) {
        if (selectedProductStockLoading) {
          return { check: { state: 'checking' }, retry: retrySelectedProductStock };
        }
        if (selectedProductStockFailed) {
          return { check: { state: 'unverified' }, retry: retrySelectedProductStock };
        }
        return {
          check: { state: 'verified', quantity: selectedProductStock?.quantity ?? 0 },
          retry: retrySelectedProductStock,
        };
      }
      return { check: { state: 'verified', quantity: product.stockQuantity ?? 0 }, retry: () => {} };
    },
    [
      selectedProduct?.id,
      currentBranchId,
      selectedProductStock,
      selectedProductStockLoading,
      selectedProductStockFailed,
      retrySelectedProductStock,
    ],
  );

  // ── Public store handlers ──
  const isCategoryRestricted = useMemo(
    () => categoryOptions.find((c) => c.id === draft.categoryId)?.isRestricted ?? false,
    [categoryOptions, draft.categoryId],
  );

  // Sync the QR visibility draft when a category needs age confirmation; the QR flow has no age check.
  useEffect(() => {
    if (isCategoryRestricted && draft.visibleOnMenuCarta !== false) {
      setDraft((prev) => ({ ...prev, visibleOnMenuCarta: false }));
    }
  }, [isCategoryRestricted, draft.visibleOnMenuCarta, setDraft]);

  const handleIsPublicChange = useCallback(
    (value: boolean) => setDraft((prev) => ({ ...prev, isPublic: value })),
    [setDraft],
  );

  // ── Service products picker (producto de evaluación) ──
  // Query dedicado: no depende de la lista paginada/filtrada del catálogo.
  // `status` distingue "no hay servicios elegibles" de "la lectura falló" —
  // el error no se convierte en lista vacía.
  const [serviceProductOptions, setServiceProductOptions] = useState<ServiceProductPickerOption[]>([]);
  const [serviceProductOptionsStatus, setServiceProductOptionsStatus] = useState<
    'idle' | 'loading' | 'ready' | 'error'
  >('idle');
  const [servicePickerEpoch, setServicePickerEpoch] = useState(0);
  useEffect(() => {
    if (!_modalVisible) {
      setServiceProductOptionsStatus('idle');
      return;
    }
    if (draft.itemType !== 'service') {
      setServiceProductOptions([]);
      setServiceProductOptionsStatus('idle');
      return;
    }
    let cancelled = false;
    setServiceProductOptionsStatus('loading');
    void listServiceProductsForPicker()
      .then((options) => {
        if (cancelled) return;
        // Excluir el producto que se está editando (no puede ser su propio evaluador).
        const editingId = editingProduct?.id;
        setServiceProductOptions(
          editingId ? options.filter((o) => o.id !== editingId) : options,
        );
        setServiceProductOptionsStatus('ready');
      })
      .catch(() => {
        if (!cancelled) {
          setServiceProductOptions([]);
          setServiceProductOptionsStatus('error');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [_modalVisible, draft.itemType, editingProduct?.id, servicePickerEpoch]);

  const retryServiceProductOptions = useCallback(() => {
    setServicePickerEpoch((n) => n + 1);
  }, []);

  const handleMenuAvailableChange = useCallback(
    (value: boolean) => {
      setDraft((prev) => ({ ...prev, menuAvailable: value }));
    },
    [setDraft],
  );

  const handleVisibleOnMenuCartaChange = useCallback(
    (value: boolean) => {
      if (value && isCategoryRestricted) return;
      setDraft((prev) => ({ ...prev, visibleOnMenuCarta: value }));
    },
    [setDraft, isCategoryRestricted],
  );

  return {
    // validation
    normalizedDraftPrice,
    codeValidationMessage,
    barcodeValidationMessage,
    priceValidationMessage,
    draftValidationMessage,
    isDraftReady,
    formCompact,
    // modal
    openCreateModal,
    expandCreateForm: () => {
      setFormCompact(false);
      const orgId = planEnt?.organization_id ?? null;
      if (orgId) {
        void writeCatalogCreateFormLayout(orgId, 'full');
      }
    },
    openDuplicateModal,
    openEditModal,
    closeModal,
    handleSave,
    handleSaveAndContinue,
    // variants
    variantDrafts,
    addVariantDraft,
    updateVariantDraft,
    removeVariantDraft,
    replaceVariantDrafts,
    generateVariantMatrix,
    // #705: variantes inactivas para reactivación
    inactiveVariants,
    handleReactivateVariant,
    variantImageUploadingKey,
    handlePickVariantImage,
    handleRemoveVariantImage,
    axisImageUploading,
    handlePickAxisImage,
    handleRemoveAxisImage,
    // detail pane
    openProductDetail,
    closeProductDetail,
    selectedProductStock,
    selectedProductStockLoading,
    selectedProductStockFailed,
    retrySelectedProductStock,
    selectedCategoryLabel,
    selectedProductMarginValue,
    selectedProductMarginPercent,
    editingProductStock,
    editingProductStockLoading,
    editingProductStockFailed,
    retryEditingProductStock,
    parentStockQty: editingProductStock?.quantity ?? null,
    editingProductCost,
    editingProductCurrentMarginValue,
    editingProductCurrentMarginPercent,
    editingProductSuggestedPrice,
    applySuggestedPrice,
    useRemoteSuggestion,
    dismissRemoteSuggestion,
    getProductStockCheck,
    formSession,
    // image
    imageUploading,
    videoUploading,
    handlePickImage,
    handleRemoveImage,
    handlePickVideo,
    handleRemoveVideo,
    // public store
    handleIsPublicChange,
    handleMenuAvailableChange,
    handleVisibleOnMenuCartaChange,
    // service products picker (producto de evaluación)
    serviceProductOptions,
    serviceProductOptionsStatus,
    retryServiceProductOptions,
    comboDraftComponents,
    setComboDraftComponents,
  };
}
